import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';

import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { cloneData, isSameData } from '../dto/data-model-utils.js';

import { BusinessModuleRef } from './business-module-ref.js';
import { ProductType } from './product.js';

export class ProductAssignment extends DataModel {
  static readonly TYPE_ID: string = 'de1632bd1862402e9a7ce690d83a86f8';

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly productId: string,
    public readonly eventId: string,
    public readonly productType: ProductType,
  ) {
    super();
  }

  get $typeId(): string {
    return ProductAssignment.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    businessRef: BusinessModuleRef,
    productId: string,
    eventId: string,
    productType: ProductType,
  }): ProductAssignment {
    return new ProductAssignment(
        args.uniqueId,
        args.businessRef,
        args.productId,
        args.eventId,
        args.productType,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ProductAssignment {
    return ProductAssignment.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      productId: MapUtils.getString(map, 'productId'),
      eventId: MapUtils.getString(map, 'eventId'),
      productType: toEnumFromString(MapUtils.getString(map, 'productType'), ProductType),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'productId': this.productId,
      'eventId': this.eventId,
      'productType': fromEnumAsString(this.productType),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    businessRef = cloneData(this.businessRef),
    productId = this.productId,
    eventId = this.eventId,
    productType = this.productType,
  }: {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    productId?: string,
    eventId?: string,
    productType?: ProductType,
  } = {}): this {
    return ProductAssignment.create({
      uniqueId,
      businessRef,
      productId,
      eventId,
      productType,
    }) as this;
  }

  isSame(other: ProductAssignment): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameData(other.businessRef, this.businessRef) &&
      other.productId === this.productId &&
      other.eventId === this.eventId &&
      other.productType === this.productType;
  }
}

export class ProductAssignmentBuilder extends DataModelBuilder<ProductAssignment> {
  build(context: SerializationContext, map: SerializedData): ProductAssignment {
    return ProductAssignment.fromMap(context, map);
  }
}
